import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/utils/supabase';
import { ProspectFilters } from './types';
import { createComponentLogger } from '@/utils/logger';
import { normalizeSearchQuery } from './search-utils';

const log = createComponentLogger('usePostClosingAnalytics', 'encompass-prospects');

export interface StageAnalytics {
  stageKey: string;
  loanCount: number;
  unitCount: number;
  loanVolume: number;
}

export interface UsePostClosingAnalyticsReturn {
  stageAnalytics: StageAnalytics[];
  isLoading: boolean;
  error: Error | null;
  refetch: (silent?: boolean) => Promise<void>;
}

const defaultStageAnalytics: StageAnalytics[] = [
  { stageKey: 'funded', loanCount: 0, unitCount: 0, loanVolume: 0 },
  { stageKey: 'ready_to_ship', loanCount: 0, unitCount: 0, loanVolume: 0 },
  { stageKey: 'delivered', loanCount: 0, unitCount: 0, loanVolume: 0 },
  { stageKey: 'conditions', loanCount: 0, unitCount: 0, loanVolume: 0 },
  { stageKey: 'cleared', loanCount: 0, unitCount: 0, loanVolume: 0 },
  { stageKey: 'pa_issued', loanCount: 0, unitCount: 0, loanVolume: 0 },
  { stageKey: 'finalized', loanCount: 0, unitCount: 0, loanVolume: 0 }
];

interface RpcRow {
  stage_key: string;
  loan_count: number;
  unit_count: number;
  loan_volume: number;
}

/**
 * Server-side analytics hook for post-closing stage summary cards.
 * Calls get_post_closing_stage_analytics RPC and returns 7 stage buckets.
 * Debounces search by 300ms. Non-search filter changes fire immediately.
 */
export function usePostClosingAnalytics(
  organizationId: string | undefined,
  filters: ProspectFilters = {},
  enabled: boolean = true
): UsePostClosingAnalyticsReturn {
  const [stageAnalytics, setStageAnalytics] = useState<StageAnalytics[]>(defaultStageAnalytics);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchAnalytics = useCallback(async (currentFilters: ProspectFilters, silent = false) => {
    if (!organizationId || !enabled) {
      if (!mountedRef.current) {
        return;
      }
      setStageAnalytics(defaultStageAnalytics);
      setIsLoading(false);
      return;
    }

    const thisRequestId = ++requestIdRef.current;
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const timer = log.startTimer('fetch_post_closing_stage_analytics');

      const { data, error: rpcError } = await supabase.rpc('get_post_closing_stage_analytics', {
        p_organization_id: organizationId,
        p_loan_folder: currentFilters.loanFolder || null,
        p_include_transferred: false,
        p_excluded_folders: currentFilters.excludedFolders?.length ? currentFilters.excludedFolders : null,
        p_search_query: normalizeSearchQuery(currentFilters.searchQuery) || null,
        p_loan_officer_id: currentFilters.loanOfficerId || null,
        p_loan_purpose: currentFilters.loanPurpose || null,
        p_created_after: currentFilters.createdAfter || null,
        p_created_before: currentFilters.createdBefore || null,
        p_min_amount: currentFilters.minAmount || null,
        p_max_amount: currentFilters.maxAmount || null,
        p_excluded_milestones: currentFilters.excludedMilestones?.length ? currentFilters.excludedMilestones : null,
        p_excluded_channels: currentFilters.excludedChannels?.length ? currentFilters.excludedChannels : null,
        p_lock_status: currentFilters.lockStatus || null,
        p_delivery_status: currentFilters.deliveryStatus || null
      });

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      if (!mountedRef.current || thisRequestId !== requestIdRef.current) {
        log.info('Discarding stale post-closing analytics response');
        return;
      }

      const rows = (Array.isArray(data) ? data : []) as RpcRow[];

      // Explicit snake_case → camelCase mapping
      const mapped: StageAnalytics[] = rows.map(row => ({
        stageKey: row.stage_key,
        loanCount: Number(row.loan_count),
        unitCount: Number(row.unit_count),
        loanVolume: Number(row.loan_volume)
      }));

      setStageAnalytics(mapped.length === 7 ? mapped : defaultStageAnalytics);
      log.endTimer(timer, { rowCount: mapped.length });
    } catch (err) {
      if (!mountedRef.current || thisRequestId !== requestIdRef.current) {
        return;
      }
      const e = err instanceof Error ? err : new Error('Failed to fetch post-closing analytics');
      log.error('Failed to fetch post-closing stage analytics', { organizationId }, e);
      setError(e);
    } finally {
      if (mountedRef.current && thisRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [organizationId, enabled]);

  // Debounce search, fire immediately for other filter changes
  const { searchQuery, ...nonSearchFilters } = filters;
  const nonSearchKey = JSON.stringify(nonSearchFilters);
  const prevSearchRef = useRef(searchQuery);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const searchChanged = prevSearchRef.current !== searchQuery;
    prevSearchRef.current = searchQuery;

    if (searchChanged && searchQuery) {
      searchTimeoutRef.current = setTimeout(() => fetchAnalytics(filters), 300);
    } else {
      fetchAnalytics(filters);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, enabled, searchQuery, nonSearchKey, fetchAnalytics]);

  // Org-level realtime subscriptions for card accuracy across ALL loans (not just loaded rows).
  // These are broader than the table's targeted subscriptions because the cards show full-pipeline totals.
  const fetchAnalyticsRef = useRef(fetchAnalytics);
  useEffect(() => {
    fetchAnalyticsRef.current = fetchAnalytics;
  }, [fetchAnalytics]);
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (!organizationId || !enabled) {
      return;
    }

    const debounceRef = { timeout: null as ReturnType<typeof setTimeout> | null };
    const scheduleRefetch = () => {
      if (debounceRef.timeout) {
        clearTimeout(debounceRef.timeout);
      }
      debounceRef.timeout = setTimeout(() => {
        fetchAnalyticsRef.current(filtersRef.current, true); // silent — no loading flash
      }, 1000);
    };

    const loansChannel = supabase
      .channel(`pc-analytics-loans-${organizationId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'encompass_loans',
        filter: `organization_id=eq.${organizationId}`
      }, scheduleRefetch)
      .subscribe();

    const trackingChannel = supabase
      .channel(`pc-analytics-tracking-${organizationId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'loan_post_closing_tracking',
        filter: `organization_id=eq.${organizationId}`
      }, scheduleRefetch)
      .subscribe();

    const reconChannel = supabase
      .channel(`pc-analytics-recon-${organizationId}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'banker_recons',
        filter: `organization_id=eq.${organizationId}`
      }, scheduleRefetch)
      .subscribe();

    const paChannel = supabase
      .channel(`pc-analytics-pa-${organizationId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'loan_documents',
        filter: `organization_id=eq.${organizationId}`
      }, (payload) => {
        const doc = payload.new as { document_type?: string };
        if (doc.document_type === 'purchase_advice') {
          scheduleRefetch();
        }
      })
      .subscribe();

    return () => {
      if (debounceRef.timeout) {
        clearTimeout(debounceRef.timeout);
      }
      supabase.removeChannel(loansChannel);
      supabase.removeChannel(trackingChannel);
      supabase.removeChannel(reconChannel);
      supabase.removeChannel(paChannel);
    };
  }, [organizationId, enabled]);

  return {
    stageAnalytics,
    isLoading,
    error,
    refetch: (silent?: boolean) => fetchAnalytics(filters, silent)
  };
}
